import { HttpClient, HttpResponseBase } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, throwError as _observableThrow } from "rxjs";
import {
	catchError as _observableCatch,
	mergeMap as _observableMergeMap,
} from 'rxjs/operators';
import { AuthenticateRequestModel, AuthenticateResponseModel } from "../models/authenticate-response.model";
import { UserModel } from "../models/authenticate-storage.model";
import { BaseService } from '../shared/base.service';
import { MainService } from '../shared/main.service';
import { API, HANDSHAKE } from "../utils/constants";
import { OptionsType } from '../utils/handshake.model';
import { AUTHENTICATE_DATA, QR_DATA } from "../utils/session.config";
import { ConfigService } from "./config.service";

@Injectable({
	providedIn: 'root',
})

export class AuthenticateService {
	API_URL: string;
	isLoggedIn = false;
	userSub = new BehaviorSubject<UserModel>(null);
	timeoutSub = new BehaviorSubject<number>(null);
	clearTimeout: any;
	warningTimeout: any;
	timeToWarning: number = 120000; // = 120 seconds, show popup warning before expirated 120s
	isHandShakeFlg: boolean;

	constructor(
		private http: HttpClient,
		private mainService: MainService,
		private baseService: BaseService,
		private configSevice: ConfigService
	) {
		this.isHandShakeFlg = this.configSevice.get(HANDSHAKE);
		this.API_URL = this.configSevice.get(API);
	}

	//#region PUBLIC FUNCTION

	public autoLogin() {
		let userData = JSON.parse(sessionStorage.getItem(AUTHENTICATE_DATA)) as UserModel;
		if (!userData) {
			return;
		}

		this.userSub.next(userData);

		let currentTime = new Date().getTime();
		let expirateTime = new Date(userData.expirateDate).getTime();

		this.autoLogout(expirateTime - currentTime);
		this.autoWarning(expirateTime - currentTime - this.timeToWarning);
	}

	public handleUserSession(response: AuthenticateResponseModel) {
		const expirateDate = new Date(
			new Date().getTime() + response.user.expritesIn * 1000
		);

		const user = new UserModel();
		user.username = response.user.username;
		user.expritesIn = response.user.expritesIn;
		user.expirateDate = expirateDate;

		this.userSub.next(user);
		sessionStorage.setItem(AUTHENTICATE_DATA, JSON.stringify(user));
		this.autoLogout(response.user.expritesIn * 1000);
		this.autoWarning(response.user.expritesIn * 1000 - this.timeToWarning);
	}

	public logout() {
		this.userSub.next(null);
		sessionStorage.removeItem(AUTHENTICATE_DATA);
		localStorage.removeItem(QR_DATA);

		if (this.clearTimeout) {
			clearTimeout(this.clearTimeout);
		}

		if (this.warningTimeout) {
			clearTimeout(this.warningTimeout);
		}
	}

	public extendExpiration() {
		let userData = JSON.parse(sessionStorage.getItem(AUTHENTICATE_DATA)) as UserModel;
		if (!userData) {
			return;
		}

		if (this.clearTimeout) {
			clearTimeout(this.clearTimeout);
		}

		const expirateDate = new Date(
			new Date().getTime() + userData.expritesIn * 1000
		);

		userData.expirateDate = expirateDate;

		sessionStorage.setItem(AUTHENTICATE_DATA, JSON.stringify(userData));
		this.autoLogout(userData.expritesIn * 1000);
		this.autoWarning(userData.expritesIn * 1000 - this.timeToWarning);
	}

	public requestLogin(model: AuthenticateRequestModel): Observable<AuthenticateResponseModel> {
		let url = this.API_URL + 'Queues/Authenticate';
		url = url.replace(/[?&]$/, "");
		let func = (endpoint: string, encryptedData: object, opts: OptionsType) => {
			let options_: any = {
				body: encryptedData,
				observe: 'response',
				responseType: 'blob',
				headers: opts.headers,
			}
			return this.http
				.request('post', endpoint, options_)
				.pipe(
					_observableMergeMap((response: any) => {
						return this.mainService.processReponse(response)
					}),
				)
				.pipe(
					_observableCatch((response: any) => {
						if (response instanceof HttpResponseBase) {
							try {
								return this.mainService.processReponse(<any>response)
							} catch (e) {
								return <Observable<any>>(<any>_observableThrow(e))
							}
						} else
							return <Observable<any>>(<any>_observableThrow(response.result))
					}),
				)
		}
		return this.baseService.handShakeAndCall<any>(
			url,
			model,
			null,
			func,
			this.isHandShakeFlg,
		)
	}

	public saveDataQR(dataQR: string) {
		if (dataQR) {
			localStorage.setItem(QR_DATA, dataQR);
		}
	}

	//#endregion PUBLIC FUNCTION

	//#region PRIVATE FUNCTION

	private autoLogout(expirationTime: number) {
		if (expirationTime < 0) {
			this.logout();
		} else {
			this.clearTimeout = setTimeout(() => {
				this.logout();
			}, expirationTime);
		}
	}

	private autoWarning(warningTime: number) {
		if (warningTime < 0) {
			return;
		}

		this.warningTimeout = setTimeout(() => {
			this.timeoutSub.next(1);
			if (this.warningTimeout) {
				clearTimeout(this.warningTimeout);
			}
		}, warningTime);
	}

	//#endregion PRIVATE FUNCTION
}